/**
 * Noticing that a remote camera has stopped sending pictures.
 *
 * A tile shows the avatar when the SFU says the producer is paused or the
 * receiving track is muted. A phone that loses its camera in the background is
 * supposed to pause (see `camera-interruption`), but that needs the phone's page
 * to still be running and its socket still open long enough to say so. When it
 * is not, nothing reaches us: the producer stays "live", the track stays
 * unmuted, and the `<video>` holds the last frame it decoded for as long as the
 * person is away. That frozen face is what Android peers were showing.
 *
 * So the tile also watches for itself: if the element's decoded-frame count
 * stops moving for a few seconds, the picture is stale and the avatar goes over
 * it. It comes off again the moment frames move.
 *
 * Deliberately cheap and deliberately narrow:
 *
 *  - **No `getStats()`.** `getVideoPlaybackQuality()` is a synchronous read off
 *    the element; a stats sweep over every consumer once a second is the cost
 *    `rtp-stats` refuses to pay unless a panel is open.
 *  - **Cameras only.** A screen share of a still slide legitimately sends no
 *    frames for minutes, so callers must not attach this to one.
 *  - **Never while our own page is hidden.** A browser may stop rendering a
 *    background tab's video, which would read as every peer freezing at once.
 *    The clock restarts when we are visible again.
 *  - **Never on a counter that has not moved.** An engine that does not count
 *    MediaStream frames reports zero forever, and that must read as "cannot
 *    tell", not as "frozen".
 */

/** The slice of an `HTMLVideoElement` this reads. */
export type FrameCountSource = {
  getVideoPlaybackQuality?: () => { totalVideoFrames: number }
}

export type FrameStallOptions = {
  onChange: (stalled: boolean) => void
  /** No new frame for this long counts as stalled. */
  stallMs?: number
  intervalMs?: number
  isPageVisible?: () => boolean
  now?: () => number
  setIntervalFn?: (fn: () => void, ms: number) => unknown
  clearIntervalFn?: (handle: unknown) => void
}

const STALL_MS = 3_000
const INTERVAL_MS = 1_000

/** Start watching. Returns a stop function, which also clears a stalled state. */
export function watchFrameStall(el: FrameCountSource, opts: FrameStallOptions): () => void {
  const stallMs = opts.stallMs ?? STALL_MS
  const intervalMs = opts.intervalMs ?? INTERVAL_MS
  const isPageVisible =
    opts.isPageVisible ?? (() => typeof document === 'undefined' || document.visibilityState === 'visible')
  const now = opts.now ?? (() => Date.now())
  const setI = opts.setIntervalFn ?? ((fn, ms) => setInterval(fn, ms))
  const clearI = opts.clearIntervalFn ?? ((h) => clearInterval(h as ReturnType<typeof setInterval>))

  const read = (): number | undefined => {
    try {
      return el.getVideoPlaybackQuality?.().totalVideoFrames
    } catch {
      return undefined
    }
  }

  const first = read()
  if (typeof first !== 'number') return () => {}

  let last = first
  let lastChange = now()
  let stalled = false

  const set = (next: boolean) => {
    if (next === stalled) return
    stalled = next
    opts.onChange(next)
  }

  const handle = setI(() => {
    const count = read()
    if (typeof count !== 'number') return
    if (count !== last) {
      last = count
      lastChange = now()
      set(false)
      return
    }
    if (!isPageVisible()) {
      lastChange = now()
      return
    }
    if (count === 0) return
    if (now() - lastChange >= stallMs) set(true)
  }, intervalMs)

  return () => {
    clearI(handle)
    set(false)
  }
}
